import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { clearPostLoginPath, resumePostLoginCapture, savePostLoginPath } from './postLoginPath';
import LoadingSpinner from '../components/LoadingSpinner.jsx';

interface ProtectedRouteProps {
  requireRole?: string | readonly string[];
  skipGroupCheck?: boolean;
  redirectTo?: string;
  children?: ReactNode;
}

export default function ProtectedRoute({
  requireRole,
  skipGroupCheck,
  redirectTo,
  children,
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, role } = useAuth();
  const location = useLocation();

  // Once an authenticated visit has arrived, the remembered post-login
  // destination has done its job — forget it so it can't hijack a later login.
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      clearPostLoginPath();
      resumePostLoginCapture();
    }
  }, [isLoading, isAuthenticated]);

  // Remember where an unauthenticated visitor was going so login can return
  // them there (AC-ERROR-7). A sessionStorage write is a side effect, so it
  // belongs in an effect rather than the render body.
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      savePostLoginPath(location);
    }
  }, [isLoading, isAuthenticated, location]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!skipGroupCheck && !role && location.pathname !== '/welcome') {
    return <Navigate to="/welcome" replace />;
  }

  const requiredRoles = requireRole
    ? Array.isArray(requireRole)
      ? requireRole
      : [requireRole as string]
    : [];
  if (requiredRoles.length > 0 && (!role || !requiredRoles.includes(role))) {
    if (redirectTo) {
      return <Navigate to={redirectTo} replace />;
    }
    return (
      <div className="access-denied">
        <h1>Access Denied</h1>
        <p>You do not have permission to view this page.</p>
      </div>
    );
  }

  return <>{children ?? <Outlet />}</>;
}
